import { Component, computed, inject, input, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideBook, lucideDraftingCompass, lucideFileText, lucidePencilRuler } from '@ng-icons/lucide';
import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { Production } from '../../../../référentiel-métier-processus/processus-aptitude/models/production.model';
import { Ressource } from '../../../models/ressource.model';
import { RessourceStore } from '../../../store/ressource.store';

type NouvelleRessource = Omit<Ressource, 'id' | 'type' | 'indice'>;

@Component({
  selector: 'app-ressource-form-item',
  imports: [
    HlmSelectImports,
    BrnSelectImports,
    HlmInputImports,
    HlmFieldImports,
    ExpansionPanel,
    ReactiveFormsModule,
    HlmAutocompleteImports,
    LabelRequiredDirective,
    HlmIconImports,
  ],
  providers: [
    provideHlmAutocompleteConfig({
      transformValueToSearch: (option: Ressource) => `${option.nom}`,
    }),
    provideIcons({ lucideBook, lucideDraftingCompass, lucidePencilRuler, lucideFileText }),
  ],
  templateUrl: './ressource-form-item.component.html',
})
export class RessourceFormItemComponent {
  readonly #ressourceStore = inject(RessourceStore);
  readonly form = input.required<FormGroup>();
  readonly idx = input.required<number>();
  readonly productions = input.required<Production[]>();

  public readonly ressourcesFiltrees = computed(() =>
    this.#ressourceStore
      .ressources()
      .filter(
        (ressource) =>
          (ressource.nom.toLowerCase().includes(this.search().toLowerCase()) ||
            `${ressource.nom}`.toLowerCase().includes(this.search().toLowerCase())) &&
          ressource.type === this.type.value,
      ),
  );
  search = signal('');
  nouvellesRessources = signal<NouvelleRessource[]>([]);
  toutesLesRessources = computed(() => [...this.ressourcesFiltrees(), ...this.nouvellesRessources()]);

  readonly ressourcesTypes = [
    { icon: 'lucideBook', label: 'Connaissance' },
    { icon: 'lucideFileText', label: 'Documentation' },
    { icon: 'lucideDraftingCompass', label: 'Habileté' },
    { icon: 'lucidePencilRuler', label: 'Matériel' },
  ];

  ajouterNouvelleRessource() {
    this.nouvellesRessources.update((current) => [...current, { nom: this.search() }]);
    const type = this.ressourcesTypes.find((x) => x.label === this.type?.value);
    this.form().patchValue({
      ressource: { nom: this.search() },
      type: type ? type.label : null,
      idsProductions: this.form().get('idsProductions')?.value,
    });
  }

  isFormInvalid() {
    return (this.form().value?.nom === '' || this.search() === '') && this.form().touched;
  }

  getIcon(type: string) {
    switch (type) {
      case 'Connaissance':
        return 'lucideBook';
      case 'Documentation':
        return 'lucideFileText';
      case 'Matériel':
        return 'lucidePencilRuler';
      case 'Habileté':
        return 'lucideDraftingCompass';
      default:
        return;
    }
  }

  /** Si le texte saisi ne correspond à aucune option, il est envoyé tel quel au formControl sous forme de nouvelle ressource. */
  onSearchChange(texte: string) {
    const nom = texte.trim();
    if (!nom || this.ressource.value?.nom === nom) {
      return;
    }

    const existant = this.#ressourceStore.ressources().find((x) => x.nom.toLowerCase() === nom.toLowerCase());
    this.ressource.setValue(existant ?? { nom }, { emitModelToViewChange: false });
  }

  get type() {
    return this.form().get('type') as FormControl<string>;
  }

  get ressource() {
    return this.form().get('ressource') as FormControl<Ressource | NouvelleRessource>;
  }
}
